'use client';

import { useState } from 'react';
import type { MouseEvent } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled, SxProps, Theme } from '../styles';
import Box from '../Box';
import Button, { Props as ButtonProps } from '../Button';
import Snackbar from '../Snackbar';
import Typography from '../Typography';
export interface Props {
  children: string;
  sx?: SxProps<Theme>;
  translations?: {
    copyButtonLabel: string;
    copyFailed: string;
    copySuccess: string;
  };
  shouldUseIcon?: boolean;
  buttonProps?: Partial<ButtonProps>;
  isMultiline?: boolean;
  isUnsafeHtmlContent?: boolean;
  displayedText?: string;
  variant?: 'box' | 'button-only';
  onCopy?: () => void;
}
const Container = styled(Box)(({
  theme: {
    palette,
    radius
  }
}) => ({
  border: `1px solid ${palette.layout.surface.border}`,
  width: '100%',
  borderRadius: radius.interaction,
  textOverflow: 'ellipsis',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: palette.form.field.background.enabled
}));
const ButtonIcon = styled(Button)(({
  theme: {
    spacing
  }
}) => ({
  padding: spacing(1),
  minWidth: 'inherit',
  '.MuiButton-startIcon': {
    margin: 0
  }
})) as typeof Button;
const CopyContent = ({
  children,
  translations = {
    copyButtonLabel: 'Copy',
    copyFailed: 'Copy failed',
    copySuccess: 'Link copied'
  },
  shouldUseIcon = false,
  buttonProps = {},
  onCopy,
  isMultiline,
  isUnsafeHtmlContent,
  displayedText,
  variant = 'box'
}: Props) => {
  const [isSnackbarOpened, setIsSnackbarOpened] = useState(false);
  const [hasError, setHasError] = useState(false);
  const handleCopy = async (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    try {
      setIsSnackbarOpened(false);
      setHasError(false);
      await window.navigator.clipboard.writeText(children?.toString() as string);
    } catch {
      setHasError(true);
    } finally {
      setIsSnackbarOpened(true);
      onCopy?.();
    }
  };
  const content = displayedText || children;
  const button = shouldUseIcon ? <ButtonIcon aria-label={translations.copyButtonLabel} variant="tertiary" onClick={handleCopy} startIcon={<ContentCopyIcon />} {...buttonProps} /> : <Button variant="tertiary" onClick={handleCopy} {...buttonProps}>
      {translations.copyButtonLabel}
    </Button>;
  const snackbar = <Snackbar type={hasError ? 'error' : 'success'} isOpen={isSnackbarOpened} onClose={/* istanbul ignore next */() => setIsSnackbarOpened(false)} message={hasError ? translations.copyFailed : translations.copySuccess} />;
  return variant === 'button-only' ? <Box>
      {button}
      {snackbar}
    </Box> : <Container sx={{
    alignItems: isMultiline ? 'center' : 'stretch',
    maxWidth: isMultiline ? 'none' : 'min-content'
  }}>
      <Typography noWrap={!isMultiline} sx={{
      p: 1,
      pl: isMultiline ? 2 : 1
    }} dangerouslySetInnerHTML={isUnsafeHtmlContent ? {
      __html: content
    } : undefined}>
        {!isUnsafeHtmlContent ? content : undefined}
      </Typography>
      {button}
      {snackbar}
    </Container>;
};
export default CopyContent;